import type { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { ApiError } from '@/api/client'
import { cn } from '@/lib/utils'
import { ButtonLink, PageHeader, Skeleton } from '@/ui'

export function errorMessage(error: unknown): string {
  if (errorCodeOf(error) === 'generator_not_configured') return 'AI 연결이 설정되지 않았다 — 설정에서 LLM을 채운다'
  return error instanceof ApiError
    ? error.message
    : error instanceof Error
      ? error.message
      : String(error)
}

/** The stable code a server answer carries, when it has one. */
export function errorCodeOf(error: unknown): string {
  if (error instanceof ApiError && error.body && typeof error.body === 'object' && 'code' in error.body) {
    return String((error.body as { code: unknown }).code)
  }
  return ''
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

export function isValidRouteID(value: number): boolean {
  return Number.isSafeInteger(value) && value > 0
}

/** The server's words, whole, where the action happened. */
export function ErrorNotice({ message }: { message: string | null }) {
  if (!message) return null
  return (
    <div
      role="alert"
      className="rounded-md border border-status-failed/30 bg-status-failed-soft/60 px-3 py-2 text-table text-status-failed"
    >
      {message}
    </div>
  )
}

export function LoadingCard({ children }: { children: ReactNode }) {
  return (
    <div role="status" className="space-y-2 rounded-lg border border-border bg-card p-4">
      <p className="text-table text-muted-foreground">{children}</p>
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-9 w-2/3" />
    </div>
  )
}

/** A centred sentence in a bordered box: an empty list. */
export function Hollow({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-border px-6 py-10 text-center text-body text-muted-foreground">
      {children}
    </div>
  )
}

export function OutreachTabs({ campaignId }: { campaignId: number }) {
  const tabs = [
    { to: `/outreach/${campaignId}/discovery`, label: '발굴' },
    { to: `/outreach/${campaignId}/queue`, label: '작업 큐' },
    { to: `/outreach/${campaignId}/analytics`, label: '성과' },
  ]
  return (
    <nav aria-label="아웃리치 캠페인 화면" className="-mx-1 flex overflow-x-auto border-b border-border px-1">
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          className={({ isActive }) =>
            cn(
              'relative shrink-0 whitespace-nowrap px-3 py-2 text-body font-medium transition-colors',
              isActive
                ? 'text-foreground after:absolute after:inset-x-3 after:-bottom-px after:h-0.5 after:bg-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )
          }
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
  )
}

/** The head of a campaign screen: the title, the way back to all campaigns,
 *  and the campaign's tabs. */
export function CampaignHeader({
  title,
  description,
  campaignId,
}: {
  title: string
  description?: string
  campaignId: number
}) {
  return (
    <PageHeader
      title={title}
      description={description}
      parent={{ to: '/outreach', label: '아웃리치' }}
      actions={
        <ButtonLink to="/outreach" size="sm">
          전체 캠페인
        </ButtonLink>
      }
    >
      <OutreachTabs campaignId={campaignId} />
    </PageHeader>
  )
}

/** A header for a route whose ID could not be read, or a screen still loading. */
export function PlainHeader({ title, description }: { title: string; description: string }) {
  return <PageHeader title={title} description={description} parent={{ to: '/outreach', label: '아웃리치' }} />
}
